import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  ChangeDetectionStrategy,
  Inject,
  PLATFORM_ID,
  OnDestroy,
  HostListener,
  signal,
  inject
} from '@angular/core';
import { SafeHtmlPipe } from "../shared/safe-html.pipe";
import { environment } from '../../environments/environment';
import { BreakpointObserver } from '@angular/cdk/layout';
import { map, Observable } from 'rxjs';
import { RouterLink, RouterModule } from "@angular/router";

interface Feature {
  title: string;
  description: string;
  iconBgColor: string;
  iconColor: string;
  delay: number;
  svg: string;
  route: string;
}

@Component({
  selector: 'app-navigation',
  standalone: true,
  imports: [CommonModule, SafeHtmlPipe, RouterLink, RouterModule],
  templateUrl: './navigation.component.html',
  styleUrl: './navigation.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavigationComponent implements OnDestroy {
  baseUrl = environment.apiUrl;
  
  // State management with signals for better performance
  isMobileMenuOpen = signal(false);
  isDesktopMenuOpen = signal(false);
  isCompanyOpen = signal(false);
  
  // Computed signal for mobile detection
  isMobile$: Observable<boolean>;
  
  private breakpointObserver = inject(BreakpointObserver);

  features: Feature[] = [
    {
      svg: `
     <svg width="39" height="39" viewBox="0 0 39 39" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect width="39" height="39" rx="19.5" fill="#E9E4FF"/>
<path d="M19.5 26.625C23.435 26.625 26.625 23.435 26.625 19.5C26.625 15.565 23.435 12.375 19.5 12.375C15.565 12.375 12.375 15.565 12.375 19.5C12.375 23.435 15.565 26.625 19.5 26.625Z" stroke="#6354A2" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M18.9514 17.71L21.875 17.125L21.29 20.0486C21.2287 20.3551 21.0782 20.6365 20.8573 20.8575C20.6364 21.0786 20.355 21.2293 20.0486 21.2908L17.125 21.875L17.71 18.9514C17.7714 18.6451 17.922 18.3638 18.1429 18.1429C18.3638 17.922 18.6451 17.7714 18.9514 17.71Z" stroke="#434343" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
    `,
      title: 'About Us',
      description: 'Discover who we are and our mission.',
      iconBgColor: 'bg-lavender',
      iconColor: 'text-lavender-icon',
      delay: 0,
      route: 'about-us'
    },
    {
      svg: `
     <svg width="39" height="39" viewBox="0 0 39 39" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect width="39" height="39" rx="19.5" fill="#E2EAFE"/>
<path d="M18.7138 27.0208H17.6039C14.7658 27.0208 13.3471 27.0208 12.4652 26.1223C11.5833 25.2238 11.5833 23.7774 11.5833 20.8854C11.5833 17.9935 11.5833 16.5471 12.4652 15.6485C13.3471 14.75 14.7658 14.75 17.6039 14.75H20.6146C23.4527 14.75 24.8722 14.75 25.7541 15.6485C26.4325 16.3397 26.5885 17.3554 26.6249 19.1042V20.2917" stroke="#4C7DFA" stroke-width="1.5" stroke-linecap="round"/>
<path d="M25.0415 24.6445H22.6665M22.6665 27.0195C22.0366 27.0195 21.4325 26.7693 20.9871 26.3239C20.5417 25.8785 20.2915 25.2744 20.2915 24.6445C20.2915 24.0146 20.5417 23.4106 20.9871 22.9652C21.4325 22.5198 22.0366 22.2695 22.6665 22.2695M25.0415 27.0195C25.6714 27.0195 26.2755 26.7693 26.7209 26.3239C27.1663 25.8785 27.4165 25.2744 27.4165 24.6445C27.4165 24.0146 27.1663 23.4106 26.7209 22.9652C26.2755 22.5198 25.6714 22.2695 25.0415 22.2695" stroke="#4C7DFA" stroke-width="1.5" stroke-linecap="round"/>
<path d="M22.6665 14.7493L22.5873 14.5039C22.1955 13.2848 21.9999 12.6752 21.5336 12.3268C21.0665 11.9785 20.4475 11.9785 19.2077 11.9785H18.9995C17.7605 11.9785 17.1407 11.9785 16.6744 12.3268C16.2073 12.6752 16.0118 13.2848 15.6199 14.5039L15.5415 14.7493" stroke="#4C7DFA" stroke-width="1.5"/>
</svg>
    `,
      title: 'Careers',
      description: 'Explore exciting opportunities and grow with us.',
      iconBgColor: 'bg-sky',
      iconColor: 'text-sky-icon',
      delay: 100,
      route: 'careers'
    },
    {
      svg: `
      <svg width="39" height="39" viewBox="0 0 39 39" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect width="39" height="39" rx="19.5" fill="#D31313" fill-opacity="0.13"/>
<path d="M16.198 22.8011C14.4955 21.0953 13.1861 19.0381 12.3616 16.7734C11.908 15.5352 12.3236 14.1751 13.2562 13.2426L13.8333 12.6662C13.9885 12.5108 14.1728 12.3874 14.3757 12.3033C14.5785 12.2191 14.796 12.1758 15.0157 12.1758C15.2353 12.1758 15.4528 12.2191 15.6557 12.3033C15.8586 12.3874 16.0429 12.5108 16.198 12.6662L17.5494 14.0176C17.7049 14.1728 17.8282 14.3571 17.9124 14.5599C17.9965 14.7628 18.0398 14.9803 18.0398 15.2C18.0398 15.4196 17.9965 15.6371 17.9124 15.84C17.8282 16.0429 17.7049 16.2272 17.5494 16.3823L17.2169 16.7148C17.0838 16.8479 16.9782 17.0059 16.9062 17.1797C16.8342 17.3536 16.7971 17.5399 16.7971 17.7281C16.7971 17.9163 16.8342 18.1027 16.9062 18.2766C16.9782 18.4504 17.0838 18.6084 17.2169 18.7415L20.2569 21.7823C20.39 21.9154 20.5479 22.0209 20.7218 22.093C20.8957 22.165 21.082 22.2021 21.2702 22.2021C21.4584 22.2021 21.6448 22.165 21.8187 22.093C21.9925 22.0209 22.1505 21.9154 22.2836 21.7823L22.6169 21.4498C22.772 21.2943 22.9563 21.171 23.1592 21.0868C23.3621 21.0026 23.5796 20.9593 23.7992 20.9593C24.0189 20.9593 24.2363 21.0026 24.4392 21.0868C24.6421 21.171 24.8264 21.2943 24.9816 21.4498L26.3329 22.8011C26.4884 22.9563 26.6118 23.1406 26.6959 23.3435C26.7801 23.5464 26.8234 23.7639 26.8234 23.9835C26.8234 24.2031 26.7801 24.4206 26.6959 24.6235C26.6118 24.8264 26.4884 25.0107 26.3329 25.1659L25.7566 25.7422C24.824 26.6756 23.4639 27.0912 22.2258 26.6376C19.9611 25.813 17.9039 24.5037 16.198 22.8011Z" stroke="#E91E63" stroke-width="1.5" stroke-linejoin="round"/>
</svg>
    `,
      title: 'Contact Us',
      description: 'Reach our team for support or inquiries anytime.',
      iconBgColor: 'bg-rose',
      iconColor: 'text-rose-icon',
      delay: 200,
      route: 'contact-us'
    },
  ];

  navLinks = [
    { name: 'Products', href: '#products' },
    { name: 'Developers', href: '#developers' },
    { name: 'Company', href: '#company' },
  ];

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    // Observe mobile/tablet breakpoints - anything below 1024px shows mobile view
    this.isMobile$ = this.breakpointObserver.observe([
      '(max-width: 1023.98px)' // Custom breakpoint to match Tailwind's lg breakpoint
    ]).pipe(
      map((result) => result.matches)
    );
  }

  // Close dropdowns when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (isPlatformBrowser(this.platformId)) {
      const target = event.target as HTMLElement;
      const navigationElement = document.querySelector('app-navigation');
      const dropdownElement = document.querySelector('.dropdown-menu');

      // Close desktop dropdown if clicking outside navigation or dropdown
      if (navigationElement && !navigationElement.contains(target)) {
        this.isDesktopMenuOpen.set(false);
      }
      
      // Close if clicking outside dropdown specifically
      if (dropdownElement && !dropdownElement.contains(target)) {
        const triggerButton = document.querySelector('[aria-haspopup="true"]');
        if (triggerButton && !triggerButton.contains(target)) {
          this.isDesktopMenuOpen.set(false);
        }
      }
    }
  }

  // Handle escape key to close menus
  @HostListener('document:keydown.escape')
  onEscapeKey() {
    if (this.isMobileMenuOpen()) {
      this.toggleMobileMenu();
    }
    if (this.isDesktopMenuOpen()) {
      this.isDesktopMenuOpen.set(false);
    }
  }

  onFeatureClick() {
    this.isCompanyOpen.set(false);
    this.isMobileMenuOpen.set(false);
    this.isDesktopMenuOpen.set(false);
    
    // Restore body scroll
    if (isPlatformBrowser(this.platformId)) {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }
  }

  // Prevent body scroll when mobile menu is open
  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    if (isPlatformBrowser(this.platformId)) {
      const width = (event.target as Window).innerWidth;
      // Close mobile menu if resized to desktop (1024px+)
      if (width >= 1024 && this.isMobileMenuOpen()) {
        this.isMobileMenuOpen.set(false);
        document.body.style.overflow = '';
        document.body.style.touchAction = '';
      }
      // Close desktop menu if resized to mobile (below 1024px)
      if (width < 1024 && this.isDesktopMenuOpen()) {
        this.isDesktopMenuOpen.set(false);
      }
    }
  }

  toggleDesktopMenu() {
    this.isDesktopMenuOpen.update(value => !value);
  }

  toggleMobileMenu() {
    this.isMobileMenuOpen.update(value => {
      const newValue = !value;
      
      // Handle body scroll lock
      if (isPlatformBrowser(this.platformId)) {
        if (newValue) {
          document.body.style.overflow = 'hidden';
          document.body.style.touchAction = 'none';
        } else {
          document.body.style.overflow = '';
          document.body.style.touchAction = '';
          // Also close company dropdown when closing mobile menu
          this.isCompanyOpen.set(false);
        }
      }
      
      return newValue;
    });
  }

  toggleCompanyDropdown() {
    this.isCompanyOpen.update(value => !value);
  }

  ngOnDestroy() {
    if (isPlatformBrowser(this.platformId)) {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }
  }
}